// MARK://dhk8/admin-shell/pages/modules/ReportDetailPage
// Detalle de un reporte: tabla del read-model + chart simple sobre la última
// columna numérica → dhk8-erp-reports (REST + mock). Permite filtrar por rango
// de fechas; los handlers de cliente aceptan { from, to } (no-op contra el stub).
import { useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { getReport, today, type DateRange } from '../../lib/clients/reports.js'
import { useAsync, asyncState } from '../../lib/use-async.js'

export default function ReportDetailPage() {
  const { id = '' } = useParams()
  const [range, setRange] = useState<DateRange>({ from: today(), to: today() })
  const { data, loading, error, reload } = useAsync((s) => getReport(id, range, s), [id, range.from, range.to])

  const chart = useMemo(() => {
    if (!data || data.rows.length === 0) return null
    // Find the last numeric column (heuristic: column whose all-row values are numbers).
    const lastNumericCol = (() => {
      for (let c = data.columns.length - 1; c >= 0; c--) {
        if (data.rows.every((r) => typeof r[c] === 'number')) return c
      }
      return -1
    })()
    if (lastNumericCol < 0) return null
    const values = data.rows.map((r) => Number(r[lastNumericCol]))
    const labels = data.rows.map((r) => String(r[0] ?? ''))
    const max = Math.max(...values, 1)
    return { labels, values, max, header: data.columns[lastNumericCol] }
  }, [data])

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <h1 className="adm-page__title">{data?.name ?? 'Reporte'}</h1>
          <p className="adm-page__sub">{data?.period ?? ''}</p>
        </div>
        <div className="adm-page__actions" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="date" className="adm-input" style={{ minHeight: 34, maxWidth: 160 }}
            value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} aria-label="Fecha desde" />
          <input type="date" className="adm-input" style={{ minHeight: 34, maxWidth: 160 }}
            value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} aria-label="Fecha hasta" />
          <button className="adm-btn adm-btn--sm" onClick={() => setRange({ from: today(), to: today() })}>Hoy</button>
          <Link to="/reports" className="adm-btn adm-btn--sm">← Reportes</Link>
        </div>
      </div>
      {asyncState(loading, error, reload)}
      {data && data.rows.length === 0 && (
        <div className="adm-card"><div className="adm-empty" data-testid="report-empty-state">Sin datos para este periodo. <Link to="/dashboard" style={{ color: 'var(--pos-tint)' }}>Procesa una venta primero</Link>.</div></div>
      )}
      {data && data.rows.length > 0 && (
        <>
          {chart && (
            <div className="adm-card" data-testid="report-chart">
              <h2 className="adm-card__title">{chart.header}</h2>
              <div role="img" aria-label={`Gráfico de barras: ${chart.header}`} style={{ display: 'flex', alignItems: 'stretch', gap: 8, height: 140, paddingTop: 8 }}>
                {chart.values.map((v, i) => (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 4, minWidth: 0 }}>
                    <div title={`${chart.labels[i]}: ${v}`} style={{ width: '100%', maxWidth: 36, height: `${(v / chart.max) * 100}%`, minHeight: 2, background: 'var(--pos-tint)', borderRadius: 'var(--pos-radius-sm, 4px)' }} />
                    <span style={{ fontSize: 'var(--pos-text-xxs, .65rem)', color: 'var(--pos-fg-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{chart.labels[i]}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="adm-card adm-card--pad0" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead><tr>{data.columns.map((c) => <th key={c} style={{ textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid var(--pos-separator)', color: 'var(--pos-fg-secondary)', fontWeight: 600, whiteSpace: 'nowrap' }}>{c}</th>)}</tr></thead>
              <tbody>
                {data.rows.map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j} style={{ padding: '10px 14px', borderBottom: '1px solid var(--pos-separator)', whiteSpace: 'nowrap' }}>{cell}</td>)}</tr>)}
                {data.totals && <tr style={{ fontWeight: 700 }}>{data.totals.map((cell, j) => <td key={j} style={{ padding: '10px 14px', borderTop: '2px solid var(--pos-separator-strong)' }}>{cell}</td>)}</tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
